/* eslint-disable prefer-object-spread */
export default class BopisAPI {

	/**
	 * Constructor of `BopisAPI`
	 */
	constructor (allowedStores) {
		this.allowedStores = allowedStores ?? [];

		this.MI_TO_KM = 1.60934;
		this.MI_TO_METER = this.MI_TO_KM * 1000;
		this.defaultRadius = this.MI_TO_METER * 9999;
		this.defaultMax = 9999;
		this.apiDaysList = [
			'Mon',
			'Tue',
			'Wed',
			'Thu',
			'Fri',
			'Sat',
			'Sun'
		];
		this.dayOfWeek = new Date().toLocaleString('en-us', {weekday: 'short'});

		this.sociAppkey = window.MOZ_SOCI_APPKEY;
		this.sociBusinessIds = window.MOZ_SOCI_BUSINESS_IDS;
		this.sociLocatorUrl = 'https://llp-renderer.meetsoci.com/rest';
		this.mivaJsonAPIUrl = window.mivaJS?.Json_Url;
		this.geocodeKey = window.GOOGLE_GEOCODE_API_KEY;
		this.geocodeEndpoint = `https://maps.googleapis.com/maps/api/geocode/json?key=${this.geocodeKey}`;

		this.cache = {
			bopisAPI: new Map(),
			geocode: new Map(),
			storeLookup: new Map()
		};

		this.defaultParameters = this.#getDefaultParams();
	}

	/**
	 * Default Parameters
	 *
	 * Default lat and long are the Rally House Sampler store 1.
	 *
	 * @returns {Object}
	 */
	#getDefaultParams () {
		return {
			appkey: this.sociAppkey,
			formdata: {
				geolocs: {
					geoloc: [
						{
							latitude: 37.73902,
							longitude: -97.24696
						}
					]
				},
				limit: 1,
				objectname: 'Locator::Store',
				order: '_distance',
				searchradius: this.defaultRadius,
				where: {
					'location.custom_fields.BusinessID': {
						in: this.sociBusinessIds
					},
					'location.custom_fields.hidelocator': {
						eq: 0
					}
				}
			}
		};
	}

	/**
	 * Looks up store by it's identifier
	 * @param  {String} identifier - Store identifier
	 * @returns {Object} Store, if found.
	 */
	async singleLocationLookup (identifier) {
		const params = structuredClone(this.defaultParameters);
		params.formdata.limit = 1;
		params.formdata.where.remote_id = {
			eq: identifier
		};
		params.formdata.searchradius = 0;

		// If User has allowed geolocation, utilize their lat + lng
		const geolocationPermissions = await navigator.permissions.query({name: 'geolocation'});
		if (geolocationPermissions.state === 'granted') {
			const getLocation = await new Promise((resolve, reject) => {
				navigator.geolocation.getCurrentPosition(resolve, reject);
			}).then((position) => {
				return position;
			}).catch(() => {
				return false;
			});

			if (getLocation !== false) {
				params.formdata.geolocs.geoloc = [
					{
						latitude: getLocation.coords.latitude,
						longitude: getLocation.coords.longitude
					}
				];
			}
		}

		const response = await this.request('/getlist', params);

		return response !== false && response.length === 1 ? response[0] : false;
	}

	/**
	 * Utilize the `geocodeEndpoint` to do an API call.
	 * @param  {Object} params
	 * @returns {Object} Lookup Results, if found.
	 */
	async geocodeLookup (params) {
		const parameters = new URLSearchParams(params).toString();

		const request = await fetch(`${this.geocodeEndpoint}&${parameters}`, {
			method: 'GET'
		});

		if (!request.ok) {
			throw Error(request.statusText);
		}

		const response = await request.json();

		if (response.status !== 'OK') {
			return false;
		}

		return response.results && response.results[0] ? response.results[0] : false;
	}

	/**
	 * Utilize the `geocodeEndpoint` to do an API call.
	 * @param  {String} term
	 * @returns {Object} Lookup Results, if found.
	 */
	async termLocationLookup (term) {
		const locationLookup = await this.geocodeLookup({
			address: term,
			components: 'country:US'
		});

		return locationLookup;
	}

	/**
	 * Looks up Lat/Lng via geocode API to return a place. Will be used in conjunction with a user sharing their location with the site.
	 * @param  {Number} lat - Latitude
	 * @param  {Number} lng - Longitude
	 * @returns {Object} Location if found
	 */
	async coordinatesLookup (lat, lng) {
		const params = structuredClone(this.defaultParameters);
		params.lat = lat;
		params.lng = lng;

		const locationLookup = await this.geocodeLookup({
			latlng: `${lat},${lng}`
		});

		return locationLookup;
	}

	/**
	 * API Call to look up a store based on the lat/lng.
	 * @param  {Number} lat - Latitude
	 * @param  {Number} lng - Longitude
	 * @returns {Array} Locations
	 */
	async storeSearch (lat, lng, radius = 99999) {
		const params = structuredClone(this.defaultParameters);
		params.formdata.limit = 9999;
		params.formdata.searchradius = radius;

		if (lat && lng) {
			params.formdata.geolocs.geoloc = [
				{
					latitude: lat,
					longitude: lng
				}
			];
		}

		const response = await this.request('/locatorsearch', params);

		if (response === false) {
			return false;
		}

		return response;
	}

	/**
	 * API Call to look up a store based on the lat/lng, with no radius limit
	 * @param  {Number} lat - Latitude
	 * @param  {Number} lng - Longitude
	 * @returns {Object} Location, if found.
	 */
	async storeSearchNoRadius (lat, lng) {
		const params = structuredClone(this.defaultParameters);
		params.formdata.limit = 1;
		params.formdata.searchradius = 99999;

		if (lat && lng) {
			params.formdata.geolocs.geoloc = [
				{
					latitude: lat,
					longitude: lng
				}
			];
		}

		const response = await this.request('/locatorsearch', params);

		if (response === false) {
			return false;
		}

		return response[0] ? response[0] : false;
	}

	/**
	 * API Call to look up a store based on the lat/lng. Determines which would should display based on the Product's inventories.
	 * @param   {Number} lat - Latitude
	 * @param   {Number} lng - Longitude
	 * @param   {Object} productInventories - Object of Product Inventories
	 * @param   {Number} quantity - Quantity to check against. Default: 1.
	 * @returns {Array} Locations.
	 */
	async productStoreSearch (lat, lng, productInventories = {}, quantity = 1) {
		// Check MozSOCi, then ping miva api and cross reference the store `identifiers`
		const locations = await this.storeSearch(lat, lng);

		if (locations === false) {
			return false;
		}

		return this.productInventoryStores(productInventories, locations, quantity);
	}

	/**
	 * Determines if the locations are eligible based on the product's inventories.
	 * @param  {Object} inventories
	 * @param  {Object} storeLocations
	 * @param   {Number} quantity - Quantity to check against. Default: 1.
	 * @returns {Array} Locations
	 */
	productInventoryStores (inventories = {}, storeLocations = {}, quantity = 1) {
		const locations = [];

		for (const store of storeLocations) {
			if (!this.storeIsAllowed(store)) {
				continue;
			}

			store.inventoryAvailable = this.productInventoryStore(inventories, store, quantity);
			locations.push(store);
		}

		return locations;
	}

	/**
	 * Determine's if the store is on the "Allowed List" for BOPIS.
	 * @param  {Object} storeLocation
	 * @returns {Boolean}
	 */
	storeIsAllowed (storeLocation = {}) {
		return this.identifierIsAllowed(storeLocation?.identifier);
	}

	identifierIsAllowed (identifier) {
		return this.allowedStores.includes(identifier.toString());
	}

	/**
	 * Checks to see how much inventory is at the current location, based on the product's inventory object.
	 * @param  {Object} inventories- {"95": 9,"107": 1}
	 * @param  {Object} storeLocation - Moz SOCi Location
	 * @param   {Number} quantity - Quantity to check against. Default: 1.
	 * @returns {Boolean}
	 */
	productInventoryStore (inventories = {}, storeLocation, quantity = 1) {
		storeLocation.productInventory = 0;

		if (inventories.hasOwnProperty(storeLocation.identifier) && this.storeIsAllowed(storeLocation)) {
			storeLocation.productInventory = parseInt(inventories[storeLocation.identifier], 10);
		}

		return Boolean(storeLocation.productInventory >= quantity);
	}

	/**
	 * Handles the API call to Moz SOCi
	 * @param {string} endpoint
	 * @param  {Object} params
	 * @returns {Object}
	 */
	async request (endpoint, params = {}) {
		const parameters = params;
		const requestBody = {
			request: parameters
		};
		const cachekey = this.getCacheKey(JSON.stringify(parameters));
		const cachedResponse = this.getCache('bopisAPI', cachekey);

		if (cachedResponse) {
			return cachedResponse;
		}

		const request = await fetch(`${this.sociLocatorUrl}${endpoint}`, {
			body: JSON.stringify(requestBody),
			headers: {
				'Content-Type': 'application/json'
			},
			method: 'POST'
		});

		if (!request.ok) {
			throw Error(request.statusText);
		}

		const response = await request.json();

		if (response.code !== 1 || response.response.collectioncount === 0) {
			return false;
		}

		const locations = response.response.collection;

		const formattedResponse = this.formatResponse(locations);
		this.setCache('bopisAPI', cachekey, formattedResponse);

		return formattedResponse;
	}

	/**
	 * Sets the Cache
	 * @param  {String} cacheName
	 * @param  {String} key
	 * @param  {String} value
	 */
	setCache (cacheName, key, value) {
		this.cache[cacheName].set(key, value);
	}

	/**
	 * Gets the Cache
	 * @param  {String}	cacheName
	 * @param  {String}	key
	 * @returns {Object|String}	Cache Value
	 */
	getCache (cacheName, key) {
		return this.cache[cacheName].get(key);
	}

	getCacheKey (jsonString) {
		try {
			const parsedObject = JSON.parse(jsonString);
			const sortedString = JSON.stringify(parsedObject, Object.keys(parsedObject).sort());

			let hash = Math.floor(Math.random() * 11);
			for (let i = 0; i < sortedString.length; i++) {
				hash = (hash * 32) + sortedString.charCodeAt(i);
			}

			const timestamp = Date.now().toString(36);
			const min = Math.pow(10, 10 - 1);
			const max = Math.pow(10, 10) - 1;
			const randomPart = Math.floor(Math.random() * (max - min + 1)) + min;
			const hashPart = Math.abs(hash).toString(16).slice(0, 16);

			const uniqueId = (timestamp + randomPart + hashPart).slice(0, 32);
			return uniqueId;
		} catch (error) {
			return `error-${error.message}`;
		}
	}

	/**
	 * Formats the response (specifically the hours) of each location
	 * @param  {Array}	response
	 * @returns {Array}
	 */
	formatResponse (response = []) {
		for (const store of response) {
			store.location.todaysHours = this.formatHours(store.location);
			store.location.link = this.generateWebsite(store.location);
			store.identifier = store.location.remote_id;
			store.name = store.location.listings_name;
		}

		// If there is a future date, do not display the location.
		const formattedResponse = response.filter((store) => {
			if (!store.location.opening_date || store.location.opening_date === '0000-00-00') {
				return true;
			}

			const futureOpeningDate = new Date(store.location.opening_date);
			const today = new Date();
			futureOpeningDate.setHours(0, 0, 0, 0);
			today.setHours(0, 0, 0, 0);

			if (today.getTime() >= futureOpeningDate.getTime()) {
				return true;
			}

			return false;
		});

		return formattedResponse;
	}

	/**
	 * Formats the `formattedHours` for the location
	 * @param  {Object}	location
	 */
	formatHours (location = {}) {
		let todaysHours = null;
		location.openingHours = [];
		const daysOfWeek = {
			1: 'monday',
			2: 'tuesday',
			3: 'wednesday',
			4: 'thursday',
			5: 'friday',
			6: 'saturday',
			7: 'sunday'
		};

		Object.keys(daysOfWeek).forEach(key => {
			const day = daysOfWeek[key];
			if (location.hours[day][0]) {
				location.openingHours.push({
					dayOfWeek: key,
					from1: location.hours[`${day}_open`] || '',
					hours: location.hours[day][0],
					to1: location.hours[`${day}_close`] || ''
				});
			}
		});

		location.formattedHours = {
			Fri: '',
			Mon: '',
			Sat: '',
			Sun: '',
			Thu: '',
			Tue: '',
			Wed: ''
		};

		for (const hour of location.openingHours) {
			const dayOfWeekIndex = parseInt(hour.dayOfWeek, 10) - 1;
			const dayOfWeek = this.apiDaysList[dayOfWeekIndex];
			const specialHours = this.determineSpecialHours(location.special_hours, parseInt(hour.dayOfWeek, 10));

			location.formattedHours[dayOfWeek] = this.formatHour(specialHours === false ? hour : specialHours);
			location.formattedHours[dayOfWeek].isToday = Boolean(this.dayOfWeek === dayOfWeek);

			if (location.formattedHours[dayOfWeek].isToday) {
				todaysHours = location.formattedHours[dayOfWeek];
			}
		}

		return todaysHours;
	}

	/**
	 * Determines if there is a special hour for that day
	 * @param  {Object}	location
	 */
	determineSpecialHours (specialOpeningHours, dayOfWeekIndex) {
		const currentDayOfWeek = new Date().getDay();
		const difference = dayOfWeekIndex >= currentDayOfWeek ? dayOfWeekIndex - currentDayOfWeek : currentDayOfWeek - dayOfWeekIndex + 6;
		const date = new Date();
		date.setDate(date.getDate() + difference);
		let formattedDate = date.toLocaleDateString('fr-CA', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric'
		});

		let specialHours = specialOpeningHours[formattedDate] ?? false;
		if (!specialHours) {
			return false;
		}

		const storeHours = specialHours[0]?.split('-') ?? [
			null,
			null
		];

		specialHours = {
			date: formattedDate,
			dayOfWeek: dayOfWeekIndex,
			from1: storeHours[0],
			to1: storeHours[1]
		};

		return specialHours;
	}

	/**
	 * Formats the hour
	 * @param  {Object}	hour
	 * @return {Object}
	 */
	formatHour (hour = {}) {
		const formattedHour = {
			close: hour.to1 ?? null,
			closed: (hour.closed || (hour.from1 === null && hour.to1 === null)) ?? 0,
			open: hour.from1 ?? null
		};

		if (formattedHour.close !== null) {
			formattedHour.expandedClose = parseFloat(formattedHour.close.split(':').join(''));
			formattedHour.formattedClose = this.formatTime(formattedHour.close);
		}

		if (formattedHour.open !== null) {
			formattedHour.expandedOpen = parseFloat(formattedHour.open.split(':').join(''));
			formattedHour.formattedOpen = this.formatTime(formattedHour.open);
		}

		return formattedHour;
	}

	/**
	 * Formats the time to HH:MM AM/PM
	 * @param  {String}	time
	 * @return {String}
	 */
	formatTime (time = '') {
		const splitTime = time.split(':');
		let hour = parseInt(splitTime[0], 10);
		const minutes = splitTime[1];
		const suffix = hour < 12 ? 'AM' : 'PM';

		if (hour === 0) {
			hour = 12;
		}

		if (hour > 12) {
			hour -= 12;
		}

		return `${hour}:${minutes} ${suffix}`;
	}

	/**
	 * Conversion for Meters to Miles
	 * @param  {Number}	meters
	 * @return {Number}
	 */
	meterToMiles (meters = 0) {
		return meters / this.MI_TO_METER;
	}

	/**
	 * Generates the website by using the Base URL, and slugifiying the City + street number. Url must end with the location ID.
	 * @return {String}
	 */
	generateWebsite (location) {
		location.website = `${window.mivaJS?.locationURLBase}/${this.slugify(location?.address?.city)}/${this.slugify(location?.address?.address_line_1)}`;
		return location.website;
	}

	/**
	 * Slugify a string
	 * @param {String} text
	 * @returns {void}
	 */
	slugify (text) {
		return text?.toLowerCase().replace(/[^\w ]+/g, '').replace(/ +/g, '-');
	}

}
